import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type {
  ResponseError,
  TokenIssueResult,
  TokenResponse,
  ConnectionChoice,
  TokenNextStepPickOrgMembershipsInner
} from '@app/types/sdk';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';
import { resolvePostLoginRedirectPath } from '@app/utils/auth-redirect';
import { createSsoRelayState } from '@app/utils/sso';
import { useAuthSession } from '@app/context/auth-session';
import { resolveErrorMessage } from '@app/utils/errors';
import {
  decodeCreationOptions,
  decodeRequestOptions,
  serializeCredential,
  type SerializedAssertionCredential,
  type SerializedAttestationCredential
} from '@app/utils/webauthn';
import type {
  DiscoveryResponse,
  PasskeyAttestationOptionsResponse,
  PasskeyAttestationVerifyResponse,
  PasskeyAssertionOptionsResponse,
  PasskeyAssertionVerifyResponse,
  PasswordRegisterResponse,
  PasswordVerifyResponse,
  TokenGrantRequest,
  DiscoveryMembership
} from '@app/types/auth';
import { getTokenResponseOrThrow } from '@app/utils/tokens';

type AuthView = 'entry' | 'verifyCode' | 'register' | 'forgotPassword' | 'ssoPicker';
type InlineMethod = 'password' | 'passkey' | 'sso';
type VerifyIntent = 'sso' | 'register' | 'forgotPassword' | null;

type SsoConnectionType = 'oidc' | 'saml';

interface SsoConnectionChoice {
  type: SsoConnectionType;
  name: string;
  startUrl?: string | null;
  connectionId?: string;
  idpInitiated?: boolean | null;
}

interface SsoTarget {
  orgId: string;
  orgName: string;
  connection: SsoConnectionChoice;
}

interface PickOrgOption {
  orgId: string;
  orgName: string;
}

type DiscoverStartDecision =
  | { nextStep: 'sso_redirect'; sso: SsoTarget }
  | { nextStep: 'picker'; choices: SsoTarget[] }
  | { nextStep: 'verify_email' };

interface PasswordVerifyPayload {
  email: string;
  password: string;
}

interface PasskeyAssertPayload {
  email: string;
  userVerification: 'required' | 'preferred' | 'discouraged';
}

interface PasskeyAssertVerifyPayload {
  challengeToken: string;
  credential: SerializedAssertionCredential;
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;
const CODE_LENGTH = 6;
const RESEND_COOLDOWN_MS = 30_000;

const createResponseError = (response: Response, message: string): ResponseError => {
  const error = new Error(message) as ResponseError;
  error.response = response;
  return error;
};

const AuthExperience = () => {
  const [view, setView] = useState<AuthView>('entry');
  const [email, setEmail] = useState('');
  const [activeMethod, setActiveMethod] = useState<InlineMethod | null>(null);
  const [verifyIntent, setVerifyIntent] = useState<VerifyIntent>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSubmitting, setPasswordSubmitting] = useState(false);

  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [passkeySubmitting, setPasskeySubmitting] = useState(false);

  const [ssoError, setSsoError] = useState<string | null>(null);
  const [ssoSubmitting, setSsoSubmitting] = useState(false);

  const [discoverDecision, setDiscoverDecision] = useState<DiscoverStartDecision | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [codeSubmitting, setCodeSubmitting] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendAvailableAt, setResendAvailableAt] = useState<number | null>(null);
  const [resendCountdown, setResendCountdown] = useState<number>(0);

  const [discovery, setDiscovery] = useState<DiscoveryResponse | null>(null);
  const [selectedMembershipId, setSelectedMembershipId] = useState<string | null>(null);

  const [registerPassword, setRegisterPassword] = useState('');
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState('');
  const [registerPasswordError, setRegisterPasswordError] = useState<string | null>(null);
  const [registerPasswordSubmitting, setRegisterPasswordSubmitting] = useState(false);
  const [registerPasskeyError, setRegisterPasskeyError] = useState<string | null>(null);
  const [registerPasskeySubmitting, setRegisterPasskeySubmitting] = useState(false);

  const [resetPasswordValue, setResetPasswordValue] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetPasswordError, setResetPasswordError] = useState<string | null>(null);
  const [resetPasswordSubmitting, setResetPasswordSubmitting] = useState(false);

  const [ssoPickerError, setSsoPickerError] = useState<string | null>(null);
  const [ssoPickerSubmitting, setSsoPickerSubmitting] = useState(false);

  const [orgPickerOpen, setOrgPickerOpen] = useState(false);
  const [orgPickerOptions, setOrgPickerOptions] = useState<PickOrgOption[]>([]);
  const [orgPickerSelection, setOrgPickerSelection] = useState<string | null>(null);
  const [orgPickerError, setOrgPickerError] = useState<string | null>(null);
  const [orgPickerSubmitting, setOrgPickerSubmitting] = useState(false);

  const configuration = useApiConfiguration();
  const basePath = configuration.basePath ?? '';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setSessionTokens, user, loading: sessionLoading } = useAuthSession();
  const [pendingTokenRequest, setPendingTokenRequest] = useState<TokenGrantRequest | null>(null);
  const [postAuthRoutingPending, setPostAuthRoutingPending] = useState(false);
  const redirectPath = useMemo(() => resolvePostLoginRedirectPath(searchParams.get('redirect')), [searchParams]);

  const postJson = useCallback(
    async <TRequest, TResponse>(path: string, payload: TRequest): Promise<TResponse> => {
      const target = `${basePath}${path}`;
      const response = await fetch(target, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw createResponseError(response, `${path} request failed`);
      }

      if (response.status === 204) {
        return {} as TResponse;
      }

      return (await response.json()) as TResponse;
    },
    [basePath]
  );

  const emailValue = useMemo(() => email.trim(), [email]);
  const emailIsValid = useMemo(() => emailPattern.test(emailValue), [emailValue]);
  const passwordIsValid = password.trim().length > 0;

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (resendAvailableAt == null) {
      setResendCountdown(0);
      return;
    }

    const updateCountdown = () => {
      const diff = Math.ceil((resendAvailableAt - Date.now()) / 1000);
      if (diff <= 0) {
        setResendCountdown(0);
        setResendAvailableAt(null);
      } else {
        setResendCountdown(diff);
      }
    };

    updateCountdown();
    const timer = window.setInterval(updateCountdown, 500);
    return () => {
      window.clearInterval(timer);
    };
  }, [resendAvailableAt]);

  const openOrgPicker = useCallback(
    (memberships: PickOrgOption[], request: TokenGrantRequest) => {
      setPendingTokenRequest(request);
      setOrgPickerOptions(memberships);
      setOrgPickerSelection(memberships[0]?.orgId ?? null);
      setOrgPickerError(null);
      setOrgPickerSubmitting(false);
      setOrgPickerOpen(true);
    },
    []
  );

  const handleTokenIssue = useCallback(
    async (request: TokenGrantRequest): Promise<TokenResponse | null> => {
      const result = await postJson<TokenGrantRequest, TokenIssueResult>('/auth/token', request);

      if ('nextStep' in result && result.nextStep === 'pickOrg') {
        const memberships = (result.memberships ?? []).map((membership: TokenNextStepPickOrgMembershipsInner): PickOrgOption => ({
          orgId: membership.orgId,
          orgName: membership.orgName ?? membership.orgId
        }));
        openOrgPicker(memberships, request);
        return null;
      }

      const token = getTokenResponseOrThrow(result);
      await setSessionTokens(token);
      setPostAuthRoutingPending(true);
      setPendingTokenRequest(null);
      setOrgPickerOpen(false);
      setOrgPickerOptions([]);
      setOrgPickerSelection(null);
      setOrgPickerError(null);
      return token;
    },
    [navigate, openOrgPicker, postJson, setSessionTokens]
  );

  useEffect(() => {
    const errorParam = searchParams.get('error');
    const oidcTx = searchParams.get('oidcTx');
    const samlTx = searchParams.get('samlTx');
    const loginTxParam = searchParams.get('loginTx');
    const emailTxParam = searchParams.get('emailTx');

    if (!errorParam && !oidcTx && !samlTx && !loginTxParam && !emailTxParam) {
      return;
    }

    const orgIdParam = searchParams.get('orgId') ?? undefined;
    const cleaned = new URLSearchParams(searchParams);
    cleaned.delete('error');
    cleaned.delete('oidcTx');
    cleaned.delete('samlTx');
    cleaned.delete('loginTx');
    cleaned.delete('emailTx');
    cleaned.delete('orgId');

    const searchString = cleaned.toString();
    navigate({ pathname: '/login', search: searchString ? `?${searchString}` : '' }, { replace: true });

    if (errorParam) {
      setSsoError(errorParam);
      return;
    }

    let request: TokenGrantRequest | null = null;

    if (oidcTx) {
      request = { grantType: 'oidcTx', oidcTx };
    } else if (samlTx) {
      request = { grantType: 'samlTx', samlTx };
    } else if (loginTxParam) {
      request = { grantType: 'loginTx', loginTx: loginTxParam };
    } else if (emailTxParam) {
      request = { grantType: 'emailTx', emailTx: emailTxParam };
    }

    if (!request) {
      return;
    }

    if (orgIdParam) {
      request.orgId = orgIdParam;
    }

    setSsoSubmitting(true);
    handleTokenIssue(request)
      .catch(async (error) => {
        const message = await resolveErrorMessage(error, 'We could not complete single sign-on.');
        setSsoError(message);
      })
      .finally(() => {
        setSsoSubmitting(false);
      });
  }, [handleTokenIssue, navigate, searchParams]);

  const requestDiscoveryStart = useCallback(async (): Promise<DiscoverStartDecision> => {
    return await postJson<{ email: string }, DiscoverStartDecision>('/auth/discover/start', {
      email: emailValue
    });
  }, [emailValue, postJson]);

  const startResendTimer = useCallback(() => {
    setResendAvailableAt(Date.now() + RESEND_COOLDOWN_MS);
  }, []);

  const resetInlineState = useCallback(() => {
    setActiveMethod(null);
    setPassword('');
    setPasswordError(null);
    setPasskeyError(null);
    setSsoError(null);
    setEmailError(null);
    setDiscoverDecision(null);
  }, []);

  const handleOpenMethod = useCallback(
    (method: InlineMethod) => {
      if (activeMethod === method) {
        resetInlineState();
        return;
      }

      setActiveMethod(method);
      setPasskeyError(null);
      setPasswordError(null);
      setSsoError(null);
      setEmailError(null);
      setView('entry');
    },
    [activeMethod, resetInlineState]
  );

  const handlePasswordSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!emailIsValid) {
      setEmailError('Enter a valid email address to continue.');
      setPasswordError(null);
      return;
    }

      if (!passwordIsValid) {
        setPasswordError('Enter your password to continue.');
        return;
      }

      setPasswordSubmitting(true);
      setPasswordError(null);

      try {
        const payload: PasswordVerifyPayload = { email: emailValue, password };
        const response = await postJson<PasswordVerifyPayload, PasswordVerifyResponse>(
          '/auth/password/verify',
          payload
        );

        if (response.loginTx) {
          await handleTokenIssue({
            grantType: 'loginTx',
            loginTx: response.loginTx
          });
          resetInlineState();
        } else {
          setPasswordError('We could not complete password sign-in. Try another method.');
        }
      } catch (error) {
        const message = await resolveErrorMessage(
          error,
          "We couldn't sign you in. Check your credentials and try again."
        );
        setPasswordError(message);
      } finally {
        setPasswordSubmitting(false);
      }
    },
    [emailIsValid, emailValue, password, passwordIsValid, handleTokenIssue, postJson, resetInlineState]
  );

  const handlePasskeyAssert = useCallback(async () => {
    if (!emailIsValid) {
      setEmailError('Enter a valid email address to continue.');
      setPasskeyError(null);
      return;
    }

    if (typeof window === 'undefined' || typeof navigator === 'undefined' || !navigator.credentials) {
      setPasskeyError('Passkey sign-in is not supported in this browser.');
      return;
    }

    setPasskeySubmitting(true);
    setPasskeyError(null);

    try {
      const optionsRequest: PasskeyAssertPayload = {
        email: emailValue,
        userVerification: 'required'
      };

      const options = await postJson<PasskeyAssertPayload, PasskeyAssertionOptionsResponse>(
        '/auth/passkeys/assert',
        optionsRequest
      );

      const publicKey = decodeRequestOptions(options.publicKey);
      const credential = (await navigator.credentials.get({ publicKey })) as PublicKeyCredential | null;

      if (!credential) {
        setPasskeyError('The passkey challenge was cancelled. Try again or choose a different method.');
        return;
      }

      const serialized = serializeCredential(credential) as SerializedAssertionCredential;
      const verifyPayload: PasskeyAssertVerifyPayload = {
        challengeToken: options.challengeToken,
        credential: serialized
      };

      const verifyResponse = await postJson<PasskeyAssertVerifyPayload, PasskeyAssertionVerifyResponse>(
        '/auth/passkeys/assert/verify',
        verifyPayload
      );

      if (verifyResponse.loginTx) {
        await handleTokenIssue({
          grantType: 'loginTx',
          loginTx: verifyResponse.loginTx
        });
        resetInlineState();
      } else {
        setPasskeyError('Passkey verification finished without sign-in. Try again or choose another method.');
      }
    } catch (error) {
      if (error instanceof DOMException) {
        setPasskeyError("We couldn't use your passkey. Try again or choose a different method.");
      } else {
        const message = await resolveErrorMessage(
          error,
          "We couldn't use your passkey. Try again or choose a different method."
        );
        setPasskeyError(message);
      }
    } finally {
      setPasskeySubmitting(false);
    }
  }, [emailIsValid, emailValue, handleTokenIssue, postJson, resetInlineState]);

  const handleSsoStart = useCallback(async () => {
    if (!emailIsValid) {
      setEmailError('Enter a valid email address to continue.');
      setSsoError(null);
      return;
    }

    setSsoSubmitting(true);
    setSsoError(null);
    setActiveMethod(null);
    setResendError(null);
    setCodeError(null);
    setVerificationCode('');
    setDiscovery(null);
    setSelectedMembershipId(null);

    try {
      const decision = await requestDiscoveryStart();
      setDiscoverDecision(decision);

      if (decision.nextStep === 'sso_redirect') {
        const startUrl = decision.sso.connection.startUrl;
        if (startUrl) {
          if (typeof window === 'undefined') {
            setSsoError('Cannot launch single sign-on outside a browser environment.');
            return;
          }
          try {
            const { encoded } = createSsoRelayState(redirectPath);
            const target = new URL(startUrl, window.location.origin);
            target.searchParams.set('state', encoded);
            target.searchParams.set('relayState', encoded);
            window.location.assign(target.toString());
          } catch (error) {
            console.warn('[sso] failed to append state to startUrl', error);
            window.location.assign(startUrl);
          }
          return;
        }

        const requiresPortal = decision.sso.connection.idpInitiated;
        setSsoError(
          requiresPortal
            ? 'Start from your identity provider portal to continue.'
            : 'SSO for this organization is unavailable. Contact your administrator.'
        );
        return;
      }

      if (decision.nextStep === 'picker') {
        setView('ssoPicker');
        setVerifyIntent('sso');
        return;
      }

      startResendTimer();
      setVerifyIntent('sso');
      setView('verifyCode');
    } catch (error) {
      const message = await resolveErrorMessage(error, 'Something went wrong. Please retry.');
      setSsoError(message);
    } finally {
      setSsoSubmitting(false);
    }
  }, [emailIsValid, redirectPath, requestDiscoveryStart, startResendTimer]);

  const handleForgotPassword = useCallback(async () => {
    if (!emailIsValid) {
      setEmailError('Enter a valid email address to reset your password.');
      setPasswordError(null);
      return;
    }

    setActiveMethod(null);
    setVerifyIntent('forgotPassword');
    setResendError(null);
    setCodeError(null);
    setVerificationCode('');
    setDiscovery(null);
    setSelectedMembershipId(null);
    setDiscoverDecision(null);

    try {
      await requestDiscoveryStart();
      startResendTimer();
      setView('verifyCode');
    } catch (error) {
      const message = await resolveErrorMessage(error, 'Something went wrong. Please retry.');
      setEmailError(message);
    }
  }, [emailIsValid, requestDiscoveryStart, startResendTimer]);

  const handleRegister = useCallback(async () => {
    if (!emailIsValid) {
      setEmailError('Enter a valid email address to create an account.');
      setPasswordError(null);
      return;
    }

    setActiveMethod(null);
    setVerifyIntent('register');
    setResendError(null);
    setCodeError(null);
    setVerificationCode('');
    setDiscovery(null);
    setSelectedMembershipId(null);
    setDiscoverDecision(null);

    try {
      await requestDiscoveryStart();
      startResendTimer();
      setView('verifyCode');
    } catch (error) {
      const message = await resolveErrorMessage(error, 'Something went wrong. Please retry.');
      setEmailError(message);
    }
  }, [emailIsValid, requestDiscoveryStart, startResendTimer]);

  const handleBackToEntry = useCallback(() => {
    setView('entry');
    setVerifyIntent(null);
    setDiscovery(null);
    setVerificationCode('');
    setCodeError(null);
    setResendError(null);
    setResendAvailableAt(null);
    setSelectedMembershipId(null);
    setRegisterPassword('');
    setRegisterPasswordConfirm('');
    setRegisterPasswordError(null);
    setRegisterPasswordSubmitting(false);
    setRegisterPasskeyError(null);
    setRegisterPasskeySubmitting(false);
    setResetPasswordValue('');
    setResetPasswordConfirm('');
    setResetPasswordError(null);
    setResetPasswordSubmitting(false);
    setSsoPickerError(null);
    setSsoPickerSubmitting(false);
    setDiscoverDecision(null);
    setPendingTokenRequest(null);
    setOrgPickerOpen(false);
    setOrgPickerOptions([]);
    setOrgPickerSelection(null);
    setOrgPickerSubmitting(false);
    setOrgPickerError(null);
    resetInlineState();
  }, [resetInlineState]);

  const handleVerifyCodeSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      const normalizedCode = verificationCode.trim();
      if (normalizedCode.length !== CODE_LENGTH) {
        setCodeError('Enter the 6-digit code to continue.');
        return;
      }

      setCodeSubmitting(true);
      setCodeError(null);

      try {
        const result = await postJson<{ email: string; code: string }, DiscoveryResponse>(
          '/auth/discover/verify',
          {
            email: emailValue,
            code: normalizedCode
          }
        );

        setDiscovery(result);
        setVerificationCode('');
        setResendAvailableAt(null);
        setResendCountdown(0);
        setResendError(null);
        setSelectedMembershipId(result.memberships[0]?.orgId ?? null);
        setDiscoverDecision(null);

        if (verifyIntent === 'register') {
          setView('register');
        } else if (verifyIntent === 'forgotPassword') {
          setView('forgotPassword');
        } else if (verifyIntent === 'sso') {
          setView('ssoPicker');
        } else {
          setView('entry');
        }
      } catch (error) {
        const message = await resolveErrorMessage(
          error,
          "That code didn't work. Request a new code or try again."
        );
        setCodeError(message);
      } finally {
        setCodeSubmitting(false);
      }
    },
    [emailValue, postJson, verificationCode, verifyIntent]
  );

  const handleResendCode = useCallback(async () => {
    if (resendAvailableAt != null) {
      return;
    }

    setResendError(null);
    setCodeError(null);
    setVerificationCode('');

    try {
      await requestDiscoveryStart();
      startResendTimer();
    } catch (error) {
      const message = await resolveErrorMessage(error, 'Something went wrong. Please retry.');
      setResendError(message);
    }
  }, [requestDiscoveryStart, resendAvailableAt, startResendTimer]);

  const allowPasswordRegistration = discovery?.localConnections?.includes('password') ?? false;
  const allowPasskeyRegistration = discovery?.localConnections?.includes('passkey') ?? false;

  const ssoChoices = useMemo<SsoTarget[]>(() => {
    if (discoverDecision?.nextStep === 'picker') {
      return discoverDecision.choices;
    }

    if (discovery) {
      const items: SsoTarget[] = [];
      discovery.memberships.forEach((membership: DiscoveryMembership) => {
        membership.connections?.forEach((connection: ConnectionChoice) => {
          items.push({
            orgId: membership.orgId,
            orgName: membership.orgName,
            connection
          });
        });
      });
      return items;
    }

    return [];
  }, [discoverDecision, discovery]);

  const selectedMembership = useMemo(() => {
    if (!discovery) {
      return null;
    }
    return discovery.memberships.find((membership: DiscoveryMembership) => membership.orgId === selectedMembershipId) ?? null;
  }, [discovery, selectedMembershipId]);

  const codeIsComplete = verificationCode.trim().length === CODE_LENGTH;

  const handleSsoContinue = useCallback(
    (target: SsoTarget) => {
      if (ssoPickerSubmitting) {
        return;
      }

      const startUrl = target.connection.startUrl;
      if (startUrl) {
        if (typeof window === 'undefined') {
          setSsoPickerError('Cannot launch single sign-on outside a browser environment.');
          return;
        }
        setSsoPickerError(null);
        setSsoPickerSubmitting(true);
        try {
          const { encoded } = createSsoRelayState(redirectPath);
          const targetUrl = new URL(startUrl, window.location.origin);
          targetUrl.searchParams.set('state', encoded);
          targetUrl.searchParams.set('relayState', encoded);
          window.location.assign(targetUrl.toString());
        } catch (error) {
          console.warn('[sso] failed to append state to startUrl', error);
          window.location.assign(startUrl);
        }
        return;
      }

      if (target.connection.idpInitiated) {
        setSsoPickerError('Start from your identity provider portal to continue.');
      } else {
        setSsoPickerError('This SSO connection is not available right now.');
      }
    },
    [redirectPath, ssoPickerSubmitting]
  );

  const handleRegisterPasswordSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!discovery?.emailTx) {
        setRegisterPasswordError('Your verification expired. Start again from the sign-in screen.');
        return;
      }

      if (!registerPassword) {
        setRegisterPasswordError('Enter a new password.');
        return;
      }

      if (registerPassword !== registerPasswordConfirm) {
        setRegisterPasswordError('Passwords do not match.');
        return;
      }

      if (registerPassword.length < 12) {
        setRegisterPasswordError('Use at least 12 characters for a strong password.');
        return;
      }

      setRegisterPasswordSubmitting(true);
      setRegisterPasswordError(null);

      try {
        const response = await postJson<{ emailTx: string; password: string }, PasswordRegisterResponse>(
          '/auth/password/register?issueLoginTx=true',
          {
            emailTx: discovery.emailTx,
            password: registerPassword
          }
        );

        if (response.loginTx) {
          await handleTokenIssue({ grantType: 'loginTx', loginTx: response.loginTx });
        } else {
          handleBackToEntry();
          setEmailError('Password created. Sign in with your new credentials.');
        }
      } catch (error) {
        const message = await resolveErrorMessage(
          error,
          "We couldn't create your account. Try again or contact your administrator."
        );
        setRegisterPasswordError(message);
      } finally {
        setRegisterPasswordSubmitting(false);
      }
    },
    [
      discovery,
      handleBackToEntry,
      handleTokenIssue,
      postJson,
      registerPassword,
      registerPasswordConfirm,
      setEmailError
    ]
  );

  const handleRegisterPasskey = useCallback(async () => {
    if (!discovery?.emailTx) {
      setRegisterPasskeyError('Your verification expired. Start again from the sign-in screen.');
      return;
    }

    if (typeof window === 'undefined' || typeof navigator === 'undefined' || !navigator.credentials) {
      setRegisterPasskeyError('Passkey registration is not supported in this browser.');
      return;
    }

    setRegisterPasskeySubmitting(true);
    setRegisterPasskeyError(null);

    try {
      const options = await postJson<{ emailTx: string }, PasskeyAttestationOptionsResponse>(
        '/auth/passkeys/attest',
        { emailTx: discovery.emailTx }
      );

      const publicKey = decodeCreationOptions(options.publicKey);
      const credential = (await navigator.credentials.create({ publicKey })) as PublicKeyCredential | null;

      if (!credential) {
        setRegisterPasskeyError('Passkey registration was cancelled. Try again or choose a different method.');
        return;
      }

      const serialized = serializeCredential(credential) as SerializedAttestationCredential;
      const verifyResponse = await postJson<
        { challengeToken: string; credential: SerializedAttestationCredential },
        PasskeyAttestationVerifyResponse
      >('/auth/passkeys/attest/verify?issueLoginTx=true', {
        challengeToken: options.challengeToken,
        credential: serialized
      });

      if (verifyResponse.loginTx) {
        await handleTokenIssue({ grantType: 'loginTx', loginTx: verifyResponse.loginTx });
      } else {
        handleBackToEntry();
        setEmailError('Passkey registered. Sign in with your new passkey.');
      }
    } catch (error) {
      if (error instanceof DOMException) {
        console.error('[passkey] navigator.credentials.create failed', { name: error.name, message: error.message });
        const friendly =
          error.name === 'NotAllowedError'
            ? 'Passkey registration was cancelled or blocked. Ensure you approve the browser prompt and that passkeys are allowed on this device.'
            : error.name === 'SecurityError'
              ? 'Passkey registration was blocked by the browser. Confirm you are on https://portal.raja.reapitnow.ai and the RP ID matches the site.'
              : 'Passkey registration did not complete. Try again or use another method.';
        setRegisterPasskeyError(friendly);
      } else {
        const message = await resolveErrorMessage(
          error,
          'We could not register your passkey. Try again or contact support.'
        );
        setRegisterPasskeyError(message);
      }
    } finally {
      setRegisterPasskeySubmitting(false);
    }
  }, [discovery, handleBackToEntry, handleTokenIssue, postJson, setEmailError]);

  const handleResetPasswordSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!discovery?.emailTx) {
        setResetPasswordError('Your verification expired. Start again from the sign-in screen.');
        return;
      }

      if (!resetPasswordValue) {
        setResetPasswordError('Enter a new password.');
        return;
      }

      if (resetPasswordValue !== resetPasswordConfirm) {
        setResetPasswordError('Passwords do not match.');
        return;
      }

      if (resetPasswordValue.length < 12) {
        setResetPasswordError('Use at least 12 characters for a strong password.');
        return;
      }

      setResetPasswordSubmitting(true);
      setResetPasswordError(null);

      try {
        const response = await postJson<{ emailTx: string; newPassword: string }, PasswordRegisterResponse>(
          '/auth/password/reset?issueLoginTx=true',
          {
            emailTx: discovery.emailTx,
            newPassword: resetPasswordValue
          }
        );

        if (response.loginTx) {
          await handleTokenIssue({ grantType: 'loginTx', loginTx: response.loginTx });
        } else {
          handleBackToEntry();
          setEmailError('Password reset. Sign in with your new password.');
        }
      } catch (error) {
        const message = await resolveErrorMessage(
          error,
          "We couldn't reset your password. Try again or contact support."
        );
        setResetPasswordError(message);
      } finally {
        setResetPasswordSubmitting(false);
      }
    },
    [
      discovery,
      handleBackToEntry,
      handleTokenIssue,
      postJson,
      resetPasswordConfirm,
      resetPasswordValue,
      setEmailError
    ]
  );

  const handleBackToVerification = useCallback(() => {
    setView('verifyCode');
  }, []);

  const handleOrgPickerCancel = useCallback(() => {
    setOrgPickerOpen(false);
    setOrgPickerError(null);
    setOrgPickerOptions([]);
    setOrgPickerSelection(null);
    setPendingTokenRequest(null);
  }, []);

  const handleOrgPickerCreate = useCallback(() => {
    setOrgPickerOpen(false);
    setOrgPickerError(null);
    setOrgPickerOptions([]);
    setOrgPickerSelection(null);
    setPendingTokenRequest(null);
    navigate('/orgs/create');
  }, [navigate]);

  const handleOrgPickerConfirm = useCallback(async () => {
    if (!pendingTokenRequest) {
      setOrgPickerError('Your session expired. Start again.');
      setOrgPickerOpen(false);
      return;
    }

    if (!orgPickerSelection) {
      setOrgPickerError('Select an organization to continue.');
      return;
    }

    setOrgPickerSubmitting(true);
    setOrgPickerError(null);

    try {
      await handleTokenIssue({
        ...pendingTokenRequest,
        orgId: orgPickerSelection
      });
      setPendingTokenRequest(null);
      setOrgPickerOpen(false);
    } catch (error) {
      const message = await resolveErrorMessage(error, 'We were unable to bind your organization.');
      setOrgPickerError(message);
    } finally {
      setOrgPickerSubmitting(false);
    }
  }, [handleTokenIssue, orgPickerSelection, pendingTokenRequest]);

  useEffect(() => {
    if (!postAuthRoutingPending) {
      return;
    }

    if (sessionLoading) {
      return;
    }

    if (!user) {
      setPostAuthRoutingPending(false);
      return;
    }

    if (!user.memberships || user.memberships.length === 0) {
      navigate('/orgs/create', { replace: true });
    } else {
      navigate(redirectPath, { replace: true });
    }

    setPostAuthRoutingPending(false);
  }, [navigate, postAuthRoutingPending, redirectPath, sessionLoading, user]);

  const renderEntryView = () => (
    <main className="auth-experience">
      <section className="auth-experience__card">
        <header className="auth-experience__header">
          <h1>Sign in</h1>
        </header>

        <div className="auth-experience__body">
          <div className="auth-field-stack">
            <input
              id="auth-email"
              name="email"
              type="email"
              autoComplete="email"
              className="auth-input"
              aria-label="Email"
              value={email}
              onChange={(event) => {
                const next = event.target.value;
                setEmail(next);
                setEmailError(null);
                setPasswordError(null);
                setPasskeyError(null);
                setSsoError(null);
                setSsoPickerError(null);
                setVerificationCode('');
                setCodeError(null);
                setResendError(null);
                setResendAvailableAt(null);
                setDiscovery(null);
                setSelectedMembershipId(null);
                setRegisterPassword('');
                setRegisterPasswordConfirm('');
                setRegisterPasswordError(null);
                setRegisterPasskeyError(null);
                setResetPasswordValue('');
                setResetPasswordConfirm('');
                setResetPasswordError(null);
                setDiscoverDecision(null);
              }}
              aria-invalid={!emailIsValid && email.length > 0}
            />
            {emailError && (
              <p className="auth-error" role="alert">
                {emailError}
              </p>
            )}

            {activeMethod === 'password' && (
              <form className="auth-inline-panel auth-inline-panel--inline" onSubmit={handlePasswordSubmit} noValidate>
                <div className="auth-inline-panel__header">
                  <button
                    type="button"
                    className="auth-inline-close"
                    onClick={resetInlineState}
                    aria-label="Close password sign-in"
                    disabled={passwordSubmitting}
                  >
                    ✕
                  </button>
                </div>

                <div className="auth-password-row">
                  <input
                    id="auth-password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    className="auth-input auth-input--password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(event) => {
                      setPassword(event.target.value);
                      setPasswordError(null);
                    }}
                    aria-invalid={passwordError != null}
                  />
                  <button type="submit" className="auth-arrow-button" disabled={!passwordIsValid || passwordSubmitting} aria-label="Submit password">
                    →
                  </button>
                </div>

                {passwordError && (
                  <p className="auth-error" role="alert">
                    {passwordError}
                  </p>
                )}
              </form>
            )}

            {activeMethod === 'passkey' && (
              <div className="auth-inline-panel auth-inline-panel--inline">
                <div className="auth-inline-panel__header">
                  <button
                    type="button"
                    className="auth-inline-close"
                    onClick={resetInlineState}
                    aria-label="Close passkey sign-in"
                    disabled={passkeySubmitting}
                  >
                    ✕
                  </button>
                </div>

                {passkeyError && (
                  <p className="auth-error" role="alert">
                    {passkeyError}
                  </p>
                )}
                <button
                  type="button"
                  className="auth-primary auth-primary--block"
                  onClick={handlePasskeyAssert}
                  disabled={passkeySubmitting}
                >
                  {passkeySubmitting ? 'Waiting…' : 'Use passkey'}
                </button>
              </div>
            )}
          </div>

          <div className="auth-methods">
            <div className="auth-methods__row">
              <button
                type="button"
                className={`auth-pill ${activeMethod === 'password' ? 'auth-pill--active' : ''}`}
                disabled={!emailIsValid || passwordSubmitting || passkeySubmitting || ssoSubmitting}
                onClick={() => handleOpenMethod('password')}
              >
                Password
              </button>
              <button
                type="button"
                className={`auth-pill ${activeMethod === 'passkey' ? 'auth-pill--active' : ''}`}
                disabled={!emailIsValid || passkeySubmitting || passwordSubmitting || ssoSubmitting}
                onClick={() => handleOpenMethod('passkey')}
              >
                Passkey
              </button>
            </div>

            <button
              type="button"
              className={`auth-method auth-method--primary ${activeMethod === 'sso' ? 'auth-method--primary-active' : ''}`}
              disabled={!emailIsValid || ssoSubmitting}
              onClick={() => {
                setActiveMethod('sso');
                handleSsoStart();
              }}
            >
              {ssoSubmitting ? 'Checking SSO…' : 'Login with SSO'}
            </button>
          </div>

          {ssoError && (
            <p className="auth-error" role="alert">
              {ssoError}
            </p>
          )}
        </div>

        <footer className="auth-experience__footer">
          <button
            type="button"
            className="auth-footer-link"
            onClick={() => {
              void handleForgotPassword();
            }}
            disabled={!emailIsValid || passwordSubmitting || passkeySubmitting || ssoSubmitting}
          >
            Forgot password?
          </button>
          <span className="auth-footer-separator" aria-hidden="true">
            ·
          </span>
          <button
            type="button"
            className="auth-footer-link"
            onClick={() => {
              void handleRegister();
            }}
            disabled={!emailIsValid || passwordSubmitting || passkeySubmitting || ssoSubmitting}
          >
            Register
          </button>
        </footer>
      </section>
    </main>
  );

  const renderVerifyView = () => (
    <main className="auth-experience">
      <section className="auth-experience__card">
        <header className="auth-experience__header">
          <h1>Verify your email</h1>
          <p>We sent a 6-digit code to {emailValue}. Enter it below to continue.</p>
        </header>

        <form className="auth-form" onSubmit={handleVerifyCodeSubmit} noValidate>
          <label className="auth-field-label" htmlFor="auth-code">
            Verification code
          </label>
          <input
            id="auth-code"
            name="code"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={CODE_LENGTH}
            className="auth-input auth-code-input"
            value={verificationCode}
            onChange={(event) => {
              const digitsOnly = event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
              setVerificationCode(digitsOnly);
              setCodeError(null);
            }}
            aria-invalid={codeError != null}
          />

          {codeError && (
            <p className="auth-error" role="alert">
              {codeError}
            </p>
          )}

          <div className="auth-inline-actions">
            <button type="submit" className="auth-primary" disabled={!codeIsComplete || codeSubmitting}>
              {codeSubmitting ? 'Verifying…' : 'Verify'}
            </button>
            <button type="button" className="auth-secondary" onClick={handleBackToEntry} disabled={codeSubmitting}>
              Back
            </button>
          </div>
        </form>

        <div className="auth-resend">
          <button
            type="button"
            className="auth-link"
            onClick={() => {
              void handleResendCode();
            }}
            disabled={resendAvailableAt != null || codeSubmitting}
          >
            {resendAvailableAt != null ? `Resend in ${resendCountdown}s` : 'Resend code'}
          </button>
          {resendError && (
            <p className="auth-error" role="alert">
              {resendError}
            </p>
          )}
        </div>
      </section>
    </main>
  );

  const renderRegisterView = () => {
    if (!discovery) {
      return (
        <main className="auth-experience">
          <section className="auth-experience__card">
            <header className="auth-experience__header">
              <h1>Verification required</h1>
              <p>Your verification session expired. Start again from the sign-in screen.</p>
            </header>
            <footer className="auth-experience__footer">
              <button type="button" className="auth-secondary" onClick={handleBackToEntry}>
                Back to Sign in
              </button>
            </footer>
          </section>
        </main>
      );
    }

    return (
      <main className="auth-experience">
        <section className="auth-experience__card">
          <header className="auth-experience__header">
            <h1>Create your account</h1>
            <p>Finish setting up access for {emailValue}.</p>
          </header>

          <div className="auth-experience__body auth-stack">
          {allowPasswordRegistration && (
            <form className="auth-section" onSubmit={handleRegisterPasswordSubmit} noValidate>
              <h2>Set Password</h2>
              <p>Choose a password you’ll use alongside your email.</p>
              <label className="auth-field-label" htmlFor="register-password">
                New password
              </label>
              <input
                id="register-password"
                name="registerPassword"
                type="password"
                autoComplete="new-password"
                className="auth-input"
                value={registerPassword}
                onChange={(event) => {
                  setRegisterPassword(event.target.value);
                  setRegisterPasswordError(null);
                }}
              />

              <label className="auth-field-label" htmlFor="register-password-confirm">
                Confirm password
              </label>
              <input
                id="register-password-confirm"
                name="registerPasswordConfirm"
                type="password"
                autoComplete="new-password"
                className="auth-input"
                value={registerPasswordConfirm}
                onChange={(event) => {
                  setRegisterPasswordConfirm(event.target.value);
                  setRegisterPasswordError(null);
                }}
              />

              {registerPasswordError && (
                <p className="auth-error" role="alert">
                  {registerPasswordError}
                </p>
              )}

              <div className="auth-inline-actions">
                <button type="submit" className="auth-primary" disabled={registerPasswordSubmitting}>
                  {registerPasswordSubmitting ? 'Creating account…' : 'Create & Sign in'}
                </button>
              </div>
            </form>
          )}

          {allowPasskeyRegistration && (
            <section className="auth-section">
              <h2>Register a Passkey</h2>
              <p>Use a passkey for one-tap sign-ins on trusted devices.</p>
              {registerPasskeyError && (
                <p className="auth-error" role="alert">
                  {registerPasskeyError}
                </p>
              )}
              <div className="auth-inline-actions">
                <button
                  type="button"
                  className="auth-primary"
                  onClick={() => {
                    void handleRegisterPasskey();
                  }}
                  disabled={registerPasskeySubmitting}
                >
                  {registerPasskeySubmitting ? 'Registering…' : 'Use a passkey'}
                </button>
              </div>
            </section>
          )}

          {!allowPasswordRegistration && !allowPasskeyRegistration && (
            <p className="auth-note">
              Local account registration is not enabled for this email. Contact your organization administrator to gain
              access.
            </p>
          )}

          {selectedMembership && selectedMembership.note && (
            <aside className="auth-note">
              <strong>{selectedMembership.orgName}</strong>: {selectedMembership.note}
            </aside>
          )}
          </div>

          <footer className="auth-experience__footer">
            <button type="button" className="auth-secondary" onClick={handleBackToVerification}>
              Back
            </button>
          </footer>
        </section>
      </main>
    );
  };

  const renderResetView = () => {
    if (!discovery) {
      return (
        <main className="auth-experience">
          <section className="auth-experience__card">
            <header className="auth-experience__header">
              <h1>Verification required</h1>
              <p>Your verification session expired. Start again from the sign-in screen.</p>
            </header>
            <footer className="auth-experience__footer">
              <button type="button" className="auth-secondary" onClick={handleBackToEntry}>
                Back to Sign in
              </button>
            </footer>
          </section>
        </main>
      );
    }

    return (
      <main className="auth-experience">
        <section className="auth-experience__card">
          <header className="auth-experience__header">
            <h1>Reset your password</h1>
            <p>Choose a new password for {emailValue}.</p>
          </header>

          <form className="auth-experience__body auth-stack" onSubmit={handleResetPasswordSubmit} noValidate>
          <label className="auth-field-label" htmlFor="reset-password">
            New password
          </label>
          <input
            id="reset-password"
            name="resetPassword"
            type="password"
            autoComplete="new-password"
            className="auth-input"
            value={resetPasswordValue}
            onChange={(event) => {
              setResetPasswordValue(event.target.value);
              setResetPasswordError(null);
            }}
          />

          <label className="auth-field-label" htmlFor="reset-password-confirm">
            Confirm password
          </label>
          <input
            id="reset-password-confirm"
            name="resetPasswordConfirm"
            type="password"
            autoComplete="new-password"
            className="auth-input"
            value={resetPasswordConfirm}
            onChange={(event) => {
              setResetPasswordConfirm(event.target.value);
              setResetPasswordError(null);
            }}
          />

          {resetPasswordError && (
            <p className="auth-error" role="alert">
              {resetPasswordError}
            </p>
          )}

          <div className="auth-inline-actions">
            <button type="submit" className="auth-primary" disabled={resetPasswordSubmitting}>
              {resetPasswordSubmitting ? 'Resetting…' : 'Reset & Sign in'}
            </button>
            <button type="button" className="auth-secondary" onClick={handleBackToVerification} disabled={resetPasswordSubmitting}>
              Back
            </button>
          </div>
          </form>
        </section>
      </main>
    );
  };

  const renderSsoPickerView = () => (
    <main className="auth-experience">
      <section className="auth-experience__card">
        <header className="auth-experience__header">
          <h1>Continue with your organization</h1>
          <p>Select the correct organization and provider to continue.</p>
        </header>

        <div className="auth-experience__body auth-stack">
          {ssoChoices.length === 0 ? (
            <p className="auth-note">
              No SSO connections are available for {emailValue}. Contact your organization administrator for help.
            </p>
          ) : (
            <ul className="auth-sso-list">
              {ssoChoices.map((choice) => {
                const key = `${choice.orgId}-${choice.connection.connectionId ?? choice.connection.name}`;
                return (
                  <li key={key} className="auth-sso-item">
                    <div className="auth-sso-details">
                      <strong>{choice.orgName}</strong>
                      <span className="auth-sso-connection">
                        {choice.connection.name} · {choice.connection.type.toUpperCase()}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="auth-primary"
                      onClick={() => handleSsoContinue(choice)}
                      disabled={ssoPickerSubmitting}
                    >
                      {ssoPickerSubmitting ? 'Starting…' : 'Continue'}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {ssoPickerError && (
            <p className="auth-error" role="alert">
              {ssoPickerError}
            </p>
          )}
        </div>

        <footer className="auth-experience__footer">
          <button type="button" className="auth-secondary" onClick={handleBackToEntry}>
            Back to Sign in
          </button>
        </footer>
      </section>
    </main>
  );

  let content: JSX.Element;
  switch (view) {
    case 'verifyCode':
      content = renderVerifyView();
      break;
    case 'register':
      content = renderRegisterView();
      break;
    case 'forgotPassword':
      content = renderResetView();
      break;
    case 'ssoPicker':
      content = renderSsoPickerView();
      break;
    case 'entry':
    default:
      content = renderEntryView();
      break;
  }

  return (
    <>
      {content}
      <TinyOrgPicker
        open={orgPickerOpen}
        options={orgPickerOptions}
        value={orgPickerSelection}
        onChange={setOrgPickerSelection}
        onCancel={handleOrgPickerCancel}
        onConfirm={handleOrgPickerConfirm}
        onCreateOrg={handleOrgPickerCreate}
        submitting={orgPickerSubmitting}
        error={orgPickerError}
      />
    </>
  );
};

interface TinyOrgPickerProps {
  open: boolean;
  options: PickOrgOption[];
  value: string | null;
  onChange: (orgId: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  onCreateOrg: () => void;
  submitting: boolean;
  error?: string | null;
}

const TinyOrgPicker = ({
  open,
  options,
  value,
  onChange,
  onConfirm,
  onCancel,
  onCreateOrg,
  submitting,
  error
}: TinyOrgPickerProps) => {
  if (!open) {
    return null;
  }

  return (
    <div className="org-picker-backdrop" role="dialog" aria-modal="true" aria-labelledby="org-picker-title">
      <div className="org-picker">
        <header className="org-picker__header">
          <h2 id="org-picker-title">Choose organization</h2>
          <button type="button" className="org-picker__close" onClick={onCancel} aria-label="Close organization picker">
            ×
          </button>
        </header>
        <div className="org-picker__body">
          {options.length === 0 ? (
            <p className="auth-note">No organizations available for selection.</p>
          ) : (
            <ul className="org-picker__list">
              {options.map((option) => (
                <li key={option.orgId} className="org-picker__item">
                  <label className="org-picker__option">
                    <input
                      type="radio"
                      name="orgPicker"
                      value={option.orgId}
                      checked={value === option.orgId}
                      onChange={() => onChange(option.orgId)}
                      disabled={submitting}
                    />
                    <span>{option.orgName}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}
        </div>
        <footer className="org-picker__footer">
          <button type="button" className="auth-secondary" onClick={onCancel} disabled={submitting}>
            Cancel
          </button>
          <button type="button" className="auth-primary" onClick={onConfirm} disabled={submitting || !value}>
            {submitting ? 'Binding…' : 'Continue'}
          </button>
        </footer>
        <div className="org-picker__create">
          <button type="button" className="auth-link-button" onClick={onCreateOrg} disabled={submitting}>
            + Create new organization
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthExperience;
